import { apiClient } from './api'

export interface ProjectWBSItem {
  id: number
  project_id: number
  parent_id: number | null
  name: string
  jira_id: string | null
  jira_type: string | null
  status: string
  progress: number
  assignee: string | null
  due_date: string | null
  effort_hours: string | null
  position: number
}

export interface ProjectWBSItemCreate {
  name: string
  parent_id?: number | null
  jira_id?: string | null
  jira_type?: string | null
  status?: string
  progress?: number
  assignee?: string | null
  due_date?: string | null
  effort_hours?: number | string | null
  position?: number
}

export const projectWbsService = {
  listItems(projectId: number): Promise<ProjectWBSItem[]> {
    return apiClient.get(`/api/v1/projects/${projectId}/wbs/items`).then((r) => r.data)
  },
  createItem(projectId: number, body: ProjectWBSItemCreate): Promise<ProjectWBSItem> {
    return apiClient.post(`/api/v1/projects/${projectId}/wbs/items`, body).then((r) => r.data)
  },
  updateItem(projectId: number, itemId: number, body: Partial<ProjectWBSItemCreate>): Promise<ProjectWBSItem> {
    return apiClient.patch(`/api/v1/projects/${projectId}/wbs/items/${itemId}`, body).then((r) => r.data)
  },
  deleteItem(projectId: number, itemId: number): Promise<void> {
    return apiClient.delete(`/api/v1/projects/${projectId}/wbs/items/${itemId}`)
  },
}

/** Build tree from flat list. Root items have parent_id null. */
export function buildWbsTree(
  items: ProjectWBSItem[]
): Array<{
  id: string
  level: number
  name: string
  jiraId: string
  jiraType: string
  status: string
  progress: number
  assignee: string
  dueDate: string
  effort: string
  children: ReturnType<typeof buildWbsTree>
}> {
  const byParent = new Map<number | null, ProjectWBSItem[]>()
  byParent.set(null, [])
  items.forEach((item) => {
    const key = item.parent_id ?? null
    if (!byParent.has(key)) byParent.set(key, [])
    byParent.get(key)!.push(item)
  })
  const sorted = (parentId: number | null) => (byParent.get(parentId) ?? []).sort((a, b) => a.position - b.position)

  function build(parentId: number | null, level: number): ReturnType<typeof buildWbsTree> {
    return sorted(parentId).map((item) => ({
      id: `WBS-${String(item.id).padStart(3, '0')}`,
      level,
      name: item.name,
      jiraId: item.jira_id ?? '',
      jiraType: item.jira_type ?? '',
      status: item.status,
      progress: item.progress,
      assignee: item.assignee ?? '',
      dueDate: item.due_date ?? '',
      effort: item.effort_hours != null ? `${item.effort_hours}h` : '',
      children: build(item.id, level + 1),
    }))
  }

  return build(null, 1)
}
